import { RealtimeChannel, RealtimeClient } from '@supabase/realtime-js'
import { getRealtimeBaseUrl } from '../utils/realtime'
import { getLoggedinUser } from '../helpers/api_helper'
import { getRealtimeAccessToken, subscribeToTokenUpdates } from './realtimeAuth'

type DashboardEvent = 'dashboard:update' | 'activity:new'

type ActivityData = {
  id: number
  date: string
  activity_title: string
  category_title: string
  icon_name?: string | null
  icon_colour?: string | null
  emissions_kgco2e: number
  quantity?: number | null
  quantity_unit?: string | null
  created_by_name?: string | null
}

type DashboardMetrics = {
  total_chats?: number
  open_chats?: number
  chat_message_count?: number
  chat_read_receipts?: number
  support_ticket_count?: number
  support_ticket_assigned?: number
  active_licenses?: number
  // Emissions data
  current_scopes?: {
    scope_1?: number
    scope_2?: number
    scope_3?: number
  }
  current_entries?: {
    scope_1?: number
    scope_2?: number
    scope_3?: number
    total?: number
  }
  base_entries?: {
    scope_1?: number
    scope_2?: number
    scope_3?: number
    total?: number
  }
  monthly_emissions?: Array<{
    year: number
    month: number
    scope_1_kgco2e: string
    scope_2_kgco2e: string
    scope_3_kgco2e: string
  }>
  base_monthly_emissions?: Array<{
    year: number
    month: number
    scope_1_kgco2e: string
    scope_2_kgco2e: string
    scope_3_kgco2e: string
  }>
  projects?: Array<any>
}

type DashboardPayload = {
  event: DashboardEvent
  business_id: number
  metrics?: DashboardMetrics
  activity?: ActivityData
  refresh?: boolean
  dispatched_at: string
}

export type DashboardListener = (payload: DashboardPayload) => void
export type ActivityListener = (activity: ActivityData) => void

class DashboardManager {
  private client: RealtimeClient | null = null
  private readonly channels = new Map<string, RealtimeChannel>()
  private readonly listeners = new Map<string, Set<DashboardListener>>()
  private readonly activityListeners = new Map<string, Set<ActivityListener>>()
  private tokenUnsubscribe: (() => void) | null = null

  constructor() {
    // Subscribe to token updates
    this.tokenUnsubscribe = subscribeToTokenUpdates((newToken) => {
      this.handleTokenUpdate(newToken)
    })
  }

  /**
   * Handle token update by updating the client's access token
   */
  private handleTokenUpdate(newToken: string): void {
    if (!this.client) {
      console.log('[realtimeDashboard] No client to update with new token')
      return
    }

    console.log('[realtimeDashboard] Updating client with new token')
    
    try {
      // Update the access token in the client
      this.client.setAuth(newToken)
      console.log('[realtimeDashboard] Client token updated successfully')
    } catch (error) {
      console.error('[realtimeDashboard] Failed to update client token:', error)
    }
  }

  private getAnonKey(): string | null {
    return (
      import.meta.env.VITE_SUPABASE_REALTIME_ANON_KEY ??
      import.meta.env.VITE_SUPABASE_ANON_KEY ??
      null
    )
  }

  private async ensureClient(): Promise<RealtimeClient | null> {
    // Check if user is authenticated before creating client
    const auth = getLoggedinUser()
    if (!auth || !auth.token) {
      console.log('[realtimeDashboard] User not authenticated, skipping client creation')
      return null
    }

    if (!this.client) {
      // Get user's JWT token for authentication
      const userToken = await getRealtimeAccessToken()
      if (!userToken) {
        console.error('[realtimeDashboard] Failed to get realtime access token')
        return null
      }

      console.log('[realtimeDashboard] Creating Realtime client with user JWT token')
      this.client = new RealtimeClient(getRealtimeBaseUrl(), {
        params: {
          apikey: userToken,  // Use user's JWT token for authentication
          vsn: '1.0.0',
        },
        accessToken: getRealtimeAccessToken,  // Provide callback for token refresh
      })
      this.client.connect()
    }

    return this.client
  }

  subscribe(businessId: number | string, listener: DashboardListener): () => void {
    const channelName = `dashboard:${businessId}`
    const existing = this.listeners.get(channelName)

    if (existing) {
      existing.add(listener)
    } else {
      this.listeners.set(channelName, new Set([listener]))
      // Create channel asynchronously
      this.createChannel(businessId).catch((error) => {
        console.error('[realtimeDashboard] Failed to create channel:', error)
      })
    }

    return () => {
      this.detach(channelName, listener)
    }
  }

  subscribeToActivityFeed(businessId: number | string, listener: ActivityListener): () => void {
    const channelName = `dashboard:${businessId}`
    const existing = this.activityListeners.get(channelName)

    if (existing) {
      existing.add(listener)
    } else {
      this.activityListeners.set(channelName, new Set([listener]))
      // Create channel asynchronously
      this.createChannel(businessId).catch((error) => {
        console.error('[realtimeDashboard] Failed to create channel:', error)
      })
    }

    return () => {
      this.detachActivity(channelName, listener)
    }
  }

  private async createChannel(businessId: number | string): Promise<RealtimeChannel | null> {
    const client = await this.ensureClient()
    if (!client) {
      return null
    }

    const channelName = `dashboard:${businessId}`
    const existing = this.channels.get(channelName)
    if (existing) {
      return existing
    }

    const channel = client.channel(channelName, {
      config: {
        broadcast: {
          ack: false,
          self: false,
        },
      },
    })

    // Listen for dashboard updates
    channel.on('broadcast', { event: 'dashboard:update' }, ({ payload }) => {
      console.log('[realtimeDashboard] Received dashboard:update event on channel', channelName, 'payload:', payload)
      this.emit(channelName, payload as DashboardPayload)
    })

    // Listen for activity feed updates
    channel.on('broadcast', { event: 'activity:new' }, ({ payload }) => {
      const typedPayload = payload as DashboardPayload
      if (typedPayload.activity) {
        this.emitActivity(channelName, typedPayload.activity)
      }
    })

    channel.subscribe((status) => {
      if (status === 'CHANNEL_ERROR') {
        console.warn(`[realtimeDashboard] Channel error on ${channelName}`)
      }
      if (status === 'SUBSCRIBED') {
        console.log(`[realtimeDashboard] Subscribed to ${channelName}`)
      }
      if (status === 'CLOSED') {
        this.channels.delete(channelName)
      }
    })

    this.channels.set(channelName, channel)
    return channel
  }

  private emit(channelName: string, payload: DashboardPayload): void {
    const listeners = this.listeners.get(channelName)
    if (!listeners || listeners.size === 0) return

    listeners.forEach((listener) => {
      try {
        listener(payload)
      } catch (error) {
        console.error('[realtimeDashboard] Listener error', error)
      }
    })
  }

  private emitActivity(channelName: string, activity: ActivityData): void {
    const listeners = this.activityListeners.get(channelName)
    if (!listeners || listeners.size === 0) return

    listeners.forEach((listener) => {
      try {
        listener(activity)
      } catch (error) {
        console.error('[realtimeDashboard] Activity listener error', error)
      }
    })
  }

  private detach(channelName: string, listener?: DashboardListener): void {
    const listeners = this.listeners.get(channelName)
    if (!listeners) return

    if (listener) {
      listeners.delete(listener)
    }

    if (listeners.size === 0) {
      this.listeners.delete(channelName)
      this.cleanupChannel(channelName)
    }
  }

  private detachActivity(channelName: string, listener?: ActivityListener): void {
    const listeners = this.activityListeners.get(channelName)
    if (!listeners) return

    if (listener) {
      listeners.delete(listener)
    }

    if (listeners.size === 0) {
      this.activityListeners.delete(channelName)
      this.cleanupChannel(channelName)
    }
  }

  private cleanupChannel(channelName: string): void {
    const hasListeners = this.listeners.has(channelName) && this.listeners.get(channelName)!.size > 0
    const hasActivityListeners = this.activityListeners.has(channelName) && this.activityListeners.get(channelName)!.size > 0

    if (!hasListeners && !hasActivityListeners) {
      const channel = this.channels.get(channelName)
      channel?.unsubscribe()
      this.channels.delete(channelName)
    }
  }

  disconnectAll(): void {
    this.channels.forEach((channel) => {
      channel.unsubscribe()
    })
    this.channels.clear()
    this.listeners.clear()
    this.activityListeners.clear()
    this.client?.disconnect()
    this.client = null

    // Unsubscribe from token updates
    if (this.tokenUnsubscribe) {
      this.tokenUnsubscribe()
      this.tokenUnsubscribe = null
    }
  }
}

const dashboardManager = new DashboardManager()

export const subscribeToDashboard = (
  businessId: number | string,
  listener: DashboardListener
): (() => void) => {
  return dashboardManager.subscribe(businessId, listener)
}

export const subscribeToActivityFeed = (
  businessId: number | string,
  listener: ActivityListener
): (() => void) => {
  return dashboardManager.subscribeToActivityFeed(businessId, listener)
}

export default dashboardManager
